import type { ComponentProps, ReactElement } from 'react';

import { useTheme } from '@emotion/react';

import type { IconComponent } from '@twistopayments/design-system/icons/constants';

import { BaseButton } from '../base-button';
import { ButtonRippleAnimation } from '../button-ripple-animation';

import type { Colors, Sizes } from './icon-button.styles';
import * as styles from './icon-button.styles';
import type { DEFAULT_TOOLTIP } from './tooltip-wrapper';
import { TooltipWrapper } from './tooltip-wrapper';

type Props = {
  color?: Colors;
  defaultTooltip?: keyof typeof DEFAULT_TOOLTIP;
  icon: IconComponent;
  size?: Sizes;
  tooltip?: string | ReactElement;
  'data-testid'?: string;
} & ComponentProps<typeof BaseButton>;

export const IconButton = ({
  color = 'primary',
  defaultTooltip,
  icon: Icon,
  size = 'medium',
  tooltip,
  ...buttonProps
}: Props) => {
  const theme = useTheme();

  const rippleColorMap: Record<Colors, string> = {
    primary: theme.palette.transparent.background,
    secondary: theme.palette.primary.lowContrast,
    negative: theme.palette.error.lowContrast,
    warning: theme.palette.warning.lowContrast,
    neutral: theme.palette.tertiary.lowContrast,
    ghost: theme.palette.tertiary.lowContrast,
    transparent: theme.palette.tertiary.lowContrast,
  };

  return (
    <ButtonRippleAnimation color={rippleColorMap[color]}>
      {({ rippleButtonStyles, ref, ripples }) => (
        <BaseButton
          ref={ref}
          css={[
            rippleButtonStyles,
            styles.colors(theme)[color],
            styles.iconButton(theme),
            styles.sizes(theme)[size],
          ]}
          data-testid="icon-button"
          {...buttonProps}>
          {ripples}
          <TooltipWrapper defaultTooltip={defaultTooltip} tooltip={tooltip}>
            <Icon color="inherit" size={size === 'small' ? '20' : '24'} />
          </TooltipWrapper>
        </BaseButton>
      )}
    </ButtonRippleAnimation>
  );
};
